import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ProcessedControl, ControlCondition } from './reports.service';
import { ControlConfigurationService } from './control-configuration.service';
import { Subscription } from 'rxjs';

interface DependencyState {
  disabled: WritableSignal<boolean>;
  visible: WritableSignal<boolean>;
}

@Injectable({ providedIn: 'root' })
export class ControlDependencyService {
  private controlConfig = inject(ControlConfigurationService);

  private subscriptions: Subscription[] = [];
  private dependencyStates = new Map<string, DependencyState>();

  evaluateCondition(
    condition: ControlCondition,
    formGroup: FormGroup,
  ): boolean {
    const formControlName = this.getFormControlName(condition.controlName);
    const controlValue = formGroup.get(formControlName)?.value;

    switch (condition.operator) {
      case 'contains':
        if (Array.isArray(controlValue)) {
          return controlValue.includes(condition.value);
        }
        if (typeof controlValue === 'string') {
          return controlValue.includes(condition.value || '');
        }
        return false;

      case 'equals':
        return controlValue === condition.value;

      case 'notEquals':
        return controlValue !== condition.value;

      case 'isEmpty':
        if (Array.isArray(controlValue)) {
          return controlValue.length === 0;
        }
        return (
          controlValue === null ||
          controlValue === undefined ||
          controlValue === ''
        );

      case 'isNotEmpty':
        if (Array.isArray(controlValue)) {
          return controlValue.length > 0;
        }
        return (
          controlValue !== null &&
          controlValue !== undefined &&
          controlValue !== ''
        );

      default:
        return true;
    }
  }

  getDependencyState(
    erfName: string,
    control: ProcessedControl,
  ): DependencyState {
    if (!this.dependencyStates.has(erfName)) {
      // Initialize with static values from control definition
      const initialDisabled = control.disabled ?? false;
      // If no visibleWhen condition, default to true (visible)
      const initialVisible = !control.visibleWhen;

      this.dependencyStates.set(erfName, {
        disabled: signal(initialDisabled),
        visible: signal(initialVisible),
      });
    }
    return this.dependencyStates.get(erfName)!;
  }

  isDisabled(erfName: string): WritableSignal<boolean> {
    const control = this.controlConfig.getControl(erfName);
    if (!control) {
      return signal(false);
    }
    return this.getDependencyState(erfName, control).disabled;
  }

  isVisible(erfName: string): WritableSignal<boolean> {
    const control = this.controlConfig.getControl(erfName);
    if (!control) {
      return signal(true);
    }
    return this.getDependencyState(erfName, control).visible;
  }

  /**
   * Sets up watchers for controls with dependencies
   * @param formGroup - The form group containing controls
   * @param controlNames - Array of control ERF names to watch for dependencies
   */
  setupDependencyWatchers(formGroup: FormGroup, controlNames: string[]): void {
    // Clear existing subscriptions
    this.clearSubscriptions();

    for (const erfName of controlNames) {
      const control = this.controlConfig.getControl(erfName);
      if (!control) continue;

      const state = this.getDependencyState(erfName, control);

      if (control.disabledWhen) {
        const dependentControlName = this.getFormControlName(
          control.disabledWhen.controlName,
        );
        const dependentFormControl = formGroup.get(dependentControlName);

        if (dependentFormControl) {
          // Initial evaluation
          const isDisabled =
            control.disabled ||
            this.evaluateCondition(control.disabledWhen, formGroup);
          state.disabled.set(isDisabled);

          if (isDisabled && control.resetValueWhenDisabled) {
            const targetControlName = this.getFormControlName(erfName);
            const targetFormControl = formGroup.get(targetControlName);
            if (targetFormControl) {
              targetFormControl.setValue(control.resetValueWhenDisabled);
            }
          }

          // Watch for changes
          const sub = dependentFormControl.valueChanges.subscribe(() => {
            const newDisabled =
              control.disabled ||
              this.evaluateCondition(control.disabledWhen!, formGroup);
            const wasDisabled = state.disabled();
            state.disabled.set(newDisabled);

            if (!wasDisabled && newDisabled && control.resetValueWhenDisabled) {
              const targetControlName = this.getFormControlName(erfName);
              const targetFormControl = formGroup.get(targetControlName);
              if (targetFormControl) {
                targetFormControl.setValue(control.resetValueWhenDisabled);
              }
            }
          });
          this.subscriptions.push(sub);
        }
      }

      // Handle visibleWhen condition
      if (control.visibleWhen) {
        const dependentControlName = this.getFormControlName(
          control.visibleWhen.controlName,
        );
        const dependentFormControl = formGroup.get(dependentControlName);

        if (dependentFormControl) {
          const isVisible = this.evaluateCondition(
            control.visibleWhen,
            formGroup,
          );
          state.visible.set(isVisible);

          // Apply resetValueWhenHidden on initial load if control is hidden
          if (!isVisible && control.resetValueWhenHidden) {
            const targetControlName = this.getFormControlName(erfName);
            const targetFormControl = formGroup.get(targetControlName);
            if (targetFormControl) {
              targetFormControl.setValue(control.resetValueWhenHidden);
            }
          }

          // Watch for changes
          const sub = dependentFormControl.valueChanges.subscribe(() => {
            const newVisible = this.evaluateCondition(
              control.visibleWhen!,
              formGroup,
            );
            const wasVisible = state.visible();
            state.visible.set(newVisible);

            if (!wasVisible && newVisible && control.defaultValue) {
              const targetControlName = this.getFormControlName(erfName);
              const targetFormControl = formGroup.get(targetControlName);
              if (targetFormControl && !targetFormControl.value) {
                // Parse date strings to Date objects for date controls
                const value = this.parseDefaultValue(
                  control.defaultValue,
                  control.dataType,
                );
                targetFormControl.setValue(value);
              }
            }

            if (wasVisible && !newVisible && control.resetValueWhenHidden) {
              const targetControlName = this.getFormControlName(erfName);
              const targetFormControl = formGroup.get(targetControlName);
              if (targetFormControl) {
                targetFormControl.setValue(control.resetValueWhenHidden);
              }
            }
          });
          this.subscriptions.push(sub);
        }
      }
    }
  }

  /**
   * Parses a default value string based on data type
   * @param value - The default value string
   * @param dataType - The control's data type
   * @returns Parsed value (Date for DATE type, original string otherwise)
   */
  private parseDefaultValue(value: string, dataType?: string): any {
    if (dataType === 'DATE' && value) {
      return new Date(value);
    }
    return value;
  }

  private getFormControlName(erfName: string): string {
    const erfToFormControlMap: Record<string, string> = {
      INCLUDE_AT_UNIT_LEVEL: 'includeAtUnitLevel',
      AS_OF_DATE: 'asOfDate',
      AS_OF_DATE_SELECT: 'asOfDateSelect',
      GROSS_NET_PAID_TYPE: 'payType',
      OUTPUT_FORMAT: 'outputFormat',
      AUTOFIT_XLS: 'autofitXls',
      DISPLAY_CURRENCY: 'displayCurrency',
      CLEARED_STATUS: 'clearedStatus',
      PAYMENT_CLEARED_FROM: 'clearedFrom',
      PAYMENT_CLEARED_THROUGH: 'clearedTo',
      ESCHEATED_STATUS: 'escheatedStatus',
      PAYMENT_ESCHEATED_FROM: 'escheatedFrom',
      PAYMENT_ESCHEATED_THROUGH: 'escheatedTo',
    };

    return erfToFormControlMap[erfName] || erfName;
  }

  clearSubscriptions(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  reset(): void {
    this.clearSubscriptions();
    this.dependencyStates.clear();
  }
}
